import React, {useContext, useState, useMemo, useCallback, useEffect} from "react";
import {useParams} from "react-router-dom";
import {Card} from "antd";
import {isEmpty} from "lodash";
import "../briefs.css";
import {BriefDetailsView} from "../CompanyBidSummary/BriefDetailsView.jsx";
import {useRequest} from "../../utils/hooks.jsx";
import {createOrderFromBid, addNoteToBidByAdmin, addNoteToBid} from "../../api/bids/bids.js";
import {useHistory, useLocation} from "react-router-dom";
import {BidPaymentModal} from "../CompanyBidSummary/BidPaymentModal.jsx";
import {BookingCompleteModal} from "../../marketplace/MarketplaceBooking/BookingCompleteModal.jsx";
import {useListenToSocket} from "../../components/socket/useListenToSocket.jsx";
import {TickerNotification} from "../../ordersCalendar/OrderTicker/index.jsx";
import {NOOP} from "../../utils/NOOP.jsx";
import {EventBus} from "../../bus/EventBus.jsx";
import {getOrderPrice} from "../../utils/calculationUtils.jsx";
import {StringBuilder} from "../../AppUtils.js";
import {MarketplaceContext} from "../../marketplace/marketplaceContext.jsx";
import {BidStatus} from "../../provider/ProviderBriefs/consts.jsx";
import {useBriefUserData} from "../hooks/useBriefUserData.js";
import {getLogger} from "../../Logger.jsx";

export const SystemBidSummary = () => {
    const {bidId} = useParams();
    const history = useHistory();
    const location = useLocation();
    const [isSocketError, setIsSocketError] = useState(null);
    const [isCreatingOrder, setIsCreatingOrder] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showBookingCompleteModal, setShowBookingCompleteModal] = useState(false);
    const [createdOrder, setCreatedOrder] = useState(null);
    const [highlightOrderPanel, setHighlightOrderPanel] = useState(null);

    const [bidInfo] = useRequest(`/api/bids/${bidId}`, "POST", null, [bidId], !!bidId);

    const [briefInfo, loadingBrief] = useRequest(
        `/api/briefs/${bidInfo?.briefId}`,
        "POST",
        null,
        [bidInfo?.briefId],
        !!bidInfo?.briefId
    );

    const {userBooking, userMe, isLoadingMe, fetchMe, companyMe, fetchCompany, isLoadingCompany, services} =
        useBriefUserData(briefInfo);

    const [providerDetails] = useRequest(
        `/api/providers/${bidInfo?.providerId}`,
        "get",
        null,
        [bidInfo?.providerId],
        !!bidInfo?.providerId,
        null,
        {}
    );

    const product = useMemo(() => {
        if (!providerDetails?.services) return null;

        return providerDetails.services.find(product => product.isBidProduct);
    }, [providerDetails]);

    const onReceivedBookingOfferSocketMessage = useCallback(message => {
        if (message.status === "success") {
            setCreatedOrder({
                eventId: message.eventId,
                proposalId: message.proposalId
            });

            setIsCreatingOrder(false);
            setShowBookingCompleteModal(true);
        } else if (message.status === "error") {
            EventBus.triggerError("server-error", {
                content: {
                    description: [
                        `Unfortunately we didn't manage to send your offer :(`,
                        `Error: ${message.errorMessage}`
                    ]
                }
            });

            setIsCreatingOrder(false);
        }
    }, []);

    useListenToSocket({eventName: "BOOKING_OFFER", onUpdate: onReceivedBookingOfferSocketMessage});
    useListenToSocket({eventName: "connect_error", onUpdate: () => setIsSocketError(true)});
    useListenToSocket({eventName: "connect", onUpdate: () => setIsSocketError(false)});

    const totalOrderPrice = useMemo(
        () =>
            getOrderPrice({
                offeredPrice: bidInfo?.price,
                deliveryPrice: bidInfo?.deliveryPrice
            })?.toFixed(0),
        [bidInfo?.price, bidInfo?.deliveryPrice]
    );

    const log = useMemo(
        () =>
            getLogger(
                {
                    bidId: bidInfo?.bidId,
                    briefId: briefInfo?.briefId,
                    companyId: companyMe?.companyId,
                    userId: userBooking?.userId,
                    providerId: bidInfo?.providerId,
                    totalBidAmount: totalOrderPrice
                },
                "SystemBidSummary"
            ),
        [
            bidInfo?.bidId,
            briefInfo?.briefId,
            companyMe?.companyId,
            userBooking?.userId,
            bidInfo?.providerId,
            totalOrderPrice
        ]
    );

    const onSendBid = useCallback(async values => {
        setShowPaymentModal(true);
    }, []);

    const handlePaymentConfirm = useCallback(
        async paymentData => {
            setShowPaymentModal(false);
            setIsCreatingOrder(true);

            const orderData = {
                bidId: bidInfo.bidId,
                paymentType: paymentData.paymentType,
                creditCard: paymentData.creditCard,
                tenBisCreditCard: paymentData.tenBisCreditCard,
                invoiceComment: paymentData.invoiceComment,
                invoicePONumber: paymentData.invoicePONumber,
                separateInvoice: paymentData.separateInvoice,
                deliveryRequests: paymentData.deliveryRequests,
                targetUserId: briefInfo?.userId,
                ...(paymentData.budgetCategoriesPriority
                    ? {budgetCategories: paymentData.budgetCategoriesPriority.map(category => ({category}))}
                    : {})
            };

            log("Creating order from bid", orderData);

            const res = await createOrderFromBid(orderData);

            if (!res.error && res.eventId) {
                setCreatedOrder({
                    eventId: res.eventId,
                    proposalId: res.proposalId
                });
                setIsCreatingOrder(false);
                setShowBookingCompleteModal(true);
            }
        },
        [bidInfo?.bidId, briefInfo, log]
    );

    const handleBookingCompleteNavigation = useCallback(
        path => {
            setShowBookingCompleteModal(false);
            history.push(path);
        },
        [history]
    );

    const onRequestCorrection = useCallback(
        async ({correctionDetails}) => {
            console.log("notes correction", correctionDetails);
            const res = await addNoteToBid({...bidInfo, note: correctionDetails, status: BidStatus.haveNote});
            if (res.error) {
                return;
            } else {
                history.push(`/system/briefs`);
            }
        },
        [bidInfo, history]
    );

    return (
        <>
            <div className="brief-container">
                {!!(isSocketError && isCreatingOrder && userMe?.features?.booking_offer_sockets) && (
                    <TickerNotification
                        contentString="קיבלנו את בקשתך, וההזמנה שלך בדרך להיכנס למערכת! אנחנו כאן, ונעדכן אותך במייל ובסמס ברגע שזה יקרה"
                        onClose={NOOP}
                    />
                )}
                <Card
                    className="brief-information-card-container"
                    bordered={false}
                    cover={
                        <div className="brief-information-card">
                            <div className="brief-information-card-top-bar-container">
                                <div className="brief-information-card-top-bar-item">
                                    <span className="title">
                                        {new StringBuilder("הצעת מחיר")
                                            .append(
                                                bidInfo?.providerDetails?.brandNames?.[0]
                                                    ? `מ${bidInfo?.providerDetails?.brandNames?.[0]}`
                                                    : ""
                                            )
                                            .toString()}
                                    </span>
                                    יש לעבור על ההצעה. לאחר אישורה, ההצעה תיכנס כהזמנה למערכת
                                </div>
                            </div>
                            <div className="brief-information-card-break-line" />
                            {!isEmpty(briefInfo) ? (
                                <div className="brief-information-card-section">
                                    <BriefDetailsView
                                        briefInfo={briefInfo}
                                        bidInfo={bidInfo}
                                        loadingBrief={loadingBrief || isCreatingOrder}
                                        onSendBid={onSendBid}
                                        onRequestCorrection={onRequestCorrection}
                                    />
                                </div>
                            ) : null}
                        </div>
                    }
                />
            </div>

            <MarketplaceContext.Provider
                value={{
                    productId: product?.productId,
                    product: product,
                    providerDetails: providerDetails,
                    userFormValues: userMe,
                    setParticipants: NOOP,
                    isProductBundle: false,
                    highlightOrderPanel,
                    setHighlightOrderPanel
                }}>
                <BidPaymentModal
                    visible={showPaymentModal}
                    onClose={() => setShowPaymentModal(false)}
                    onSave={handlePaymentConfirm}
                    totalBidAmount={totalOrderPrice}
                    isLoading={isCreatingOrder}
                    me={userBooking}
                    company={companyMe}
                    services={services}
                    brief={briefInfo}
                    bid={bidInfo}
                    log={log}
                />
            </MarketplaceContext.Provider>

            <BookingCompleteModal
                visible={showBookingCompleteModal}
                eventId={createdOrder?.eventId}
                proposalId={createdOrder?.proposalId}
                onSave={handleBookingCompleteNavigation}
                onClose={() => setShowBookingCompleteModal(false)}
                company={companyMe}
                me={userBooking}
            />
        </>
    );
};
